import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { CalendarClock, Plus, Pencil, Trash2, Download, Filter, Mail, ChevronDown, ChevronRight, List, Calendar as CalendarIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import { EmailScheduleModal } from '../../components/EmailScheduleModal';
import { ActionMenu } from '../../components/crm/CrmListShell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = { party_host: 'Party Host', manager: 'Manager', staff: 'Staff' };
const ROLE_COLORS = { party_host: '#FF3B6B', manager: '#6366F1', staff: '#06D6A0' };

const todayStr = () => new Date().toISOString().split('T')[0];
const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

export const CrmShifts = () => {
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editShift, setEditShift] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  // Filters
  const [filterEmp, setFilterEmp] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStart, setFilterStart] = useState(todayStr());
  const [filterEnd, setFilterEnd] = useState(plusDays(30));
  const [preset, setPreset] = useState('next30');

  // View
  const [view, setView] = useState(() => localStorage.getItem('shiftsView') || 'list');
  const [expanded, setExpanded] = useState(() => {
    try { return JSON.parse(localStorage.getItem('shiftsExpanded') || '{}'); } catch { return {}; }
  });
  const [searchName, setSearchName] = useState('');
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [dayModal, setDayModal] = useState(null); // { date, shifts }

  useEffect(() => { localStorage.setItem('shiftsView', view); }, [view]);
  useEffect(() => { localStorage.setItem('shiftsExpanded', JSON.stringify(expanded)); }, [expanded]);

  const toggleDay = (date) => setExpanded(prev => ({ ...prev, [date]: !prev[date] }));
  const expandAll = () => {
    const all = {};
    const set = new Set(shifts.map(s => s.date));
    for (const d of set) all[d] = true;
    setExpanded(all);
  };
  const collapseAll = () => setExpanded({});

  // Create form
  const [form, setForm] = useState({
    user_id: '',
    date: todayStr(),
    start_time: '09:00',
    end_time: '17:00',
    role: 'staff',
    booking_id: '',
    notes: '',
  });

  useEffect(() => { fetchAll(); }, [filterEmp, filterStart, filterEnd]);

  // Preset → date range
  useEffect(() => {
    if (preset === 'custom') return;
    const d = new Date();
    const fmt = (dd) => dd.toISOString().split('T')[0];
    let from = new Date(d), to = new Date(d);
    if (preset === 'today') { /* from = to = today */ }
    else if (preset === 'tomorrow') { from.setDate(d.getDate() + 1); to.setDate(d.getDate() + 1); }
    else if (preset === 'next7') { to.setDate(d.getDate() + 6); }
    else if (preset === 'nextWeek') {
      const dow = (d.getDay() + 6) % 7;
      from.setDate(d.getDate() + (7 - dow)); to = new Date(from); to.setDate(from.getDate() + 6);
    }
    else if (preset === 'next30') { to.setDate(d.getDate() + 29); }
    else if (preset === 'thisMonth') { from.setDate(1); to = new Date(d.getFullYear(), d.getMonth() + 1, 0); }
    setFilterStart(fmt(from)); setFilterEnd(fmt(to));
  }, [preset]);

  const fetchAll = async () => {
    try {
      const params = new URLSearchParams();
      if (filterEmp && filterEmp !== 'all') params.append('user_id', filterEmp);
      if (filterStart) params.append('start_date', filterStart);
      if (filterEnd) params.append('end_date', filterEnd);

      const [shiftRes, empRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/shifts?${params}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/employees`, { withCredentials: true }),
      ]);
      setShifts(shiftRes.data);
      setEmployees(empRes.data);
      if (!form.user_id && empRes.data.length > 0) {
        setForm(f => ({ ...f, user_id: empRes.data[0].user_id }));
      }
    } catch (e) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => setForm({
    user_id: employees[0]?.user_id || '',
    date: todayStr(),
    start_time: '09:00',
    end_time: '17:00',
    role: 'staff',
    booking_id: '',
    notes: '',
  });

  const createShift = async () => {
    if (!form.user_id || !form.date || !form.start_time || !form.end_time) {
      toast.error('Employee, date, start and end time are required');
      return;
    }
    // Client-side availability warning
    const emp = employees.find(e => e.user_id === form.user_id);
    const dayIdx = new Date(form.date + 'T12:00:00').getDay();
    const weekday = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][dayIdx];
    if (emp?.availability && emp.availability.length > 0 && !emp.availability.includes(weekday)) {
      const ok = window.confirm(
        `${emp.name} is not usually available on ${weekday.charAt(0).toUpperCase() + weekday.slice(1)}s\n` +
        `(available: ${emp.availability.join(', ')}).\n\nCreate this shift anyway?`
      );
      if (!ok) return;
    }
    try {
      const res = await axios.post(`${API_URL}/api/admin/shifts`, form, { withCredentials: true });
      if (res.data?.availability_warning) {
        toast.warning(res.data.availability_warning, { duration: 6000 });
      } else {
        toast.success('Shift created');
      }
      setCreateOpen(false);
      resetForm();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const updateShift = async () => {
    if (!editShift) return;
    try {
      await axios.patch(`${API_URL}/api/admin/shifts/${editShift.shift_id}`, {
        date: editShift.date,
        start_time: editShift.start_time,
        end_time: editShift.end_time,
        role: editShift.role,
        booking_id: editShift.booking_id,
        notes: editShift.notes,
        status: editShift.status,
      }, { withCredentials: true });
      toast.success('Shift updated');
      setEditOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deleteShift = async (shiftId) => {
    if (!window.confirm('Delete this shift?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/shifts/${shiftId}`, { withCredentials: true });
      toast.success('Shift deleted');
      fetchAll();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const exportCSV = () => {
    if (!shifts.length) { toast.error('No shifts to export'); return; }
    const headers = 'Employee,Role,Date,Start,End,Hours,Linked Booking,Notes,Status';
    const rows = shifts.map(s => {
      const [sh, sm] = s.start_time.split(':').map(Number);
      const [eh, em] = s.end_time.split(':').map(Number);
      const mins = (eh * 60 + em) - (sh * 60 + sm);
      const hours = (mins / 60).toFixed(2);
      return [
        `"${s.employee_name}"`,
        ROLE_LABELS[s.role] || s.role,
        s.date,
        s.start_time,
        s.end_time,
        hours,
        s.booking_id || '',
        `"${(s.notes || '').replace(/"/g, '""')}"`,
        s.status || 'scheduled',
      ].join(',');
    });
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `shifts_${todayStr()}.csv`;
    a.click();
    toast.success('CSV exported');
  };

  const filteredShifts = useMemo(() => {
    return shifts.filter(s => {
      if (filterRole !== 'all' && s.role !== filterRole) return false;
      if (filterStatus === 'open' && s.status === 'completed') return false;
      if (filterStatus === 'completed' && s.status !== 'completed') return false;
      if (searchName.trim() && !(s.employee_name || '').toLowerCase().includes(searchName.toLowerCase())) return false;
      return true;
    });
  }, [shifts, filterRole, filterStatus, searchName]);

  const grouped = useMemo(() => {
    const g = {};
    for (const s of filteredShifts) {
      g[s.date] = g[s.date] || [];
      g[s.date].push(s);
    }
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredShifts]);

  const totalHours = useMemo(() => {
    let mins = 0;
    for (const s of filteredShifts) {
      const [sh, sm] = s.start_time.split(':').map(Number);
      const [eh, em] = s.end_time.split(':').map(Number);
      mins += (eh * 60 + em) - (sh * 60 + sm);
    }
    return (mins / 60).toFixed(1);
  }, [filteredShifts]);

  // Auto-expand today by default (once shifts loaded)
  useEffect(() => {
    const today = todayStr();
    if (grouped.length > 0 && Object.keys(expanded).length === 0) {
      setExpanded({ [today]: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped.length]);

  // Day-level hour totals for calendar view
  const dayTotals = useMemo(() => {
    const map = {};
    for (const [date, list] of grouped) {
      let mins = 0;
      for (const s of list) {
        const [sh, sm] = s.start_time.split(':').map(Number);
        const [eh, em] = s.end_time.split(':').map(Number);
        mins += (eh * 60 + em) - (sh * 60 + sm);
      }
      map[date] = { count: list.length, hours: Number((mins / 60).toFixed(1)) };
    }
    return map;
  }, [grouped]);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>;

  return (
    <div>
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Shift Schedule</h1>
          <p className="text-[#6B6B7A]">{shifts.length} shifts scheduled · {totalHours}h total</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setEmailModalOpen(true)} variant="outline" className="rounded-full border-[#E8E5DF]" data-testid="email-schedule-btn">
            <Mail className="w-4 h-4 mr-2" /> Email Schedule
          </Button>
          <Button onClick={exportCSV} variant="outline" className="rounded-full border-[#E8E5DF]" data-testid="export-shifts-csv">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={() => { resetForm(); setCreateOpen(true); }} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="add-shift-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Shift
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 mb-6 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-[#6B6B7A]">Employee</Label>
          <Select value={filterEmp} onValueChange={setFilterEmp}>
            <SelectTrigger className="w-44 mt-1 rounded-xl border-[#E8E5DF] bg-white" data-testid="filter-employee"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Employees</SelectItem>
              {employees.map(e => <SelectItem key={e.user_id} value={e.user_id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-[#6B6B7A]">Date Range</Label>
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-40 mt-1 rounded-xl border-[#E8E5DF] bg-white" data-testid="shifts-preset"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="tomorrow">Tomorrow</SelectItem>
              <SelectItem value="next7">Next 7 days</SelectItem>
              <SelectItem value="nextWeek">Next week (Mon–Sun)</SelectItem>
              <SelectItem value="next30">Next 30 days</SelectItem>
              <SelectItem value="thisMonth">This month</SelectItem>
              <SelectItem value="custom">Custom range</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-[#6B6B7A]">From</Label>
          <Input type="date" value={filterStart} onChange={e => { setPreset('custom'); setFilterStart(e.target.value); }} className="w-40 mt-1 rounded-xl border-[#E8E5DF]" data-testid="filter-start" />
        </div>
        <div>
          <Label className="text-xs text-[#6B6B7A]">To</Label>
          <Input type="date" value={filterEnd} onChange={e => { setPreset('custom'); setFilterEnd(e.target.value); }} className="w-40 mt-1 rounded-xl border-[#E8E5DF]" data-testid="filter-end" />
        </div>
        <div>
          <Label className="text-xs text-[#6B6B7A]">Role</Label>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-32 mt-1 rounded-xl border-[#E8E5DF] bg-white" data-testid="filter-role"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="party_host">Party Host</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="staff">Staff</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-[#6B6B7A]">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32 mt-1 rounded-xl border-[#E8E5DF] bg-white" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open only</SelectItem>
              <SelectItem value="completed">Completed only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-end gap-2">
          {/* View toggle */}
          <div className="inline-flex rounded-full border border-[#E8E5DF] bg-white p-0.5" role="tablist">
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 transition-colors ${view === 'list' ? 'bg-[#FF3B6B] text-white' : 'text-[#6B6B7A] hover:text-[#1E1E24]'}`}
              data-testid="view-list"
            >
              <List className="w-3 h-3" /> List
            </button>
            <button
              onClick={() => setView('calendar')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 transition-colors ${view === 'calendar' ? 'bg-[#FF3B6B] text-white' : 'text-[#6B6B7A] hover:text-[#1E1E24]'}`}
              data-testid="view-calendar"
            >
              <CalendarIcon className="w-3 h-3" /> Calendar
            </button>
          </div>
          {view === 'list' && (
            <>
              <Button variant="outline" size="sm" onClick={expandAll} className="rounded-full border-[#E8E5DF] text-xs h-8" data-testid="expand-all-btn">Expand All</Button>
              <Button variant="outline" size="sm" onClick={collapseAll} className="rounded-full border-[#E8E5DF] text-xs h-8" data-testid="collapse-all-btn">Collapse All</Button>
            </>
          )}
        </div>
      </div>

      {view === 'list' && (
        <>
          {/* Search within */}
          <div className="mb-3 flex items-center gap-3">
            <Input value={searchName} onChange={e => setSearchName(e.target.value)} placeholder="Search by employee name…"
              className="w-64 rounded-xl border-[#E8E5DF] bg-white" data-testid="search-employee-name" />
            <span className="text-xs text-[#9a9aa6]">{grouped.length} day(s) · {filteredShifts.length} shifts</span>
          </div>

          {/* Accordion list */}
          {grouped.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[#E8E5DF] p-12 text-center">
              <CalendarClock className="w-12 h-12 text-[#6B6B7A] mx-auto mb-4" />
              <p className="text-[#6B6B7A]">No shifts match the selected filters.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {grouped.map(([date, dayShifts]) => {
                const t = dayTotals[date] || { count: 0, hours: 0 };
                const isOpen = !!expanded[date];
                const isToday = date === todayStr();
                const allCompleted = dayShifts.every(s => s.status === 'completed');
                return (
                  <div key={date} className={`bg-white rounded-2xl border ${isToday ? 'border-[#FF3B6B]/60 ring-2 ring-[#FF3B6B]/10' : 'border-[#E8E5DF]'} overflow-hidden`}>
                    <div className="flex items-center">
                      <button
                        onClick={() => toggleDay(date)}
                        className="flex-1 flex items-center justify-between px-6 py-3 bg-[#FFFDF8] hover:bg-[#FFF8E7] transition-colors text-left"
                        data-testid={`day-header-${date}`}
                      >
                        <div className="flex items-center gap-3">
                          {isOpen ? <ChevronDown className="w-4 h-4 text-[#6B6B7A]" /> : <ChevronRight className="w-4 h-4 text-[#6B6B7A]" />}
                          <div>
                            <p className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
                              {new Date(date + 'T00:00:00').toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' })}
                              {isToday && <span className="ml-2 text-xs px-2 py-0.5 bg-[#FF3B6B] text-white rounded-full">Today</span>}
                            </p>
                            <p className="text-xs text-[#6B6B7A]">{t.count} shift{t.count !== 1 ? 's' : ''} · {t.hours}h total · {allCompleted ? '🟢 All done' : '🟡 Some open'}</p>
                          </div>
                        </div>
                      </button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); resetForm(); setForm(f => ({ ...f, date })); setCreateOpen(true); }}
                        className="mr-3 h-8 text-xs rounded-full text-[#FF3B6B] hover:bg-[#FF3B6B]/10"
                        data-testid={`add-shift-for-${date}`}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add shift
                      </Button>
                    </div>
                    {isOpen && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Employee</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Start</TableHead>
                            <TableHead>End</TableHead>
                            <TableHead>Hours</TableHead>
                            <TableHead>Booking</TableHead>
                            <TableHead>Notes</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dayShifts.map(s => {
                            const [sh, sm] = s.start_time.split(':').map(Number);
                            const [eh, em] = s.end_time.split(':').map(Number);
                            const hours = (((eh * 60 + em) - (sh * 60 + sm)) / 60).toFixed(2);
                            return (
                              <TableRow key={s.shift_id} data-testid={`shift-row-${s.shift_id}`}>
                                <TableCell className="font-medium">{s.employee_name}</TableCell>
                                <TableCell>
                                  <Badge style={{ backgroundColor: `${ROLE_COLORS[s.role] || '#6B6B7A'}20`, color: ROLE_COLORS[s.role] || '#6B6B7A', border: 'none' }}>
                                    {ROLE_LABELS[s.role] || s.role}
                                  </Badge>
                                </TableCell>
                                <TableCell className="font-mono">{s.start_time}</TableCell>
                                <TableCell className="font-mono">{s.end_time}</TableCell>
                                <TableCell className="font-semibold">{hours}h</TableCell>
                                <TableCell className="text-xs font-mono text-[#6B6B7A]">{s.booking_id || '-'}</TableCell>
                                <TableCell className="max-w-[160px] truncate text-xs text-[#6B6B7A]">{s.notes || '-'}</TableCell>
                                <TableCell className="text-right">
                                  <ActionMenu
                                    testid={`shift-actions-${s.shift_id}`}
                                    items={[
                                      { label: 'Edit shift', icon: Pencil, onClick: () => { setEditShift({ ...s }); setEditOpen(true); }, testid: `edit-shift-${s.shift_id}` },
                                      { separator: true },
                                      { label: 'Delete', icon: Trash2, destructive: true, onClick: () => deleteShift(s.shift_id), testid: `del-shift-${s.shift_id}` },
                                    ]}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {view === 'calendar' && (
        <CalendarMonthView
          year={calMonth.year}
          month={calMonth.month}
          onPrev={() => setCalMonth(m => {
            const d = new Date(m.year, m.month - 1, 1);
            return { year: d.getFullYear(), month: d.getMonth() };
          })}
          onNext={() => setCalMonth(m => {
            const d = new Date(m.year, m.month + 1, 1);
            return { year: d.getFullYear(), month: d.getMonth() };
          })}
          onToday={() => { const d = new Date(); setCalMonth({ year: d.getFullYear(), month: d.getMonth() }); }}
          dayTotals={dayTotals}
          grouped={grouped}
          onDayClick={(date, list) => setDayModal({ date, shifts: list })}
        />
      )}

      {/* Day modal for calendar view */}
      <Dialog open={!!dayModal} onOpenChange={(o) => !o && setDayModal(null)}>
        <DialogContent className="max-w-3xl bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>
              Shifts for {dayModal?.date ? new Date(dayModal.date + 'T00:00:00').toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' }) : ''}
            </DialogTitle>
          </DialogHeader>
          {dayModal && (
            <div className="space-y-3">
              <Button size="sm" onClick={() => { resetForm(); setForm(f => ({ ...f, date: dayModal.date })); setCreateOpen(true); setDayModal(null); }}
                className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="modal-add-shift">
                <Plus className="w-3 h-3 mr-1" /> Add shift
              </Button>
              {dayModal.shifts.length === 0 ? (
                <p className="text-center text-[#6B6B7A] py-6">No shifts scheduled.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Start</TableHead>
                      <TableHead>End</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Booking</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dayModal.shifts.map(s => {
                      const [sh, sm] = s.start_time.split(':').map(Number);
                      const [eh, em] = s.end_time.split(':').map(Number);
                      const hours = (((eh * 60 + em) - (sh * 60 + sm)) / 60).toFixed(2);
                      return (
                        <TableRow key={s.shift_id}>
                          <TableCell className="font-medium">{s.employee_name}</TableCell>
                          <TableCell>
                            <Badge style={{ backgroundColor: `${ROLE_COLORS[s.role] || '#6B6B7A'}20`, color: ROLE_COLORS[s.role] || '#6B6B7A', border: 'none' }}>
                              {ROLE_LABELS[s.role] || s.role}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono">{s.start_time}</TableCell>
                          <TableCell className="font-mono">{s.end_time}</TableCell>
                          <TableCell className="font-semibold">{hours}h</TableCell>
                          <TableCell className="text-xs font-mono text-[#6B6B7A]">{s.booking_id || '-'}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => { setEditShift({ ...s }); setEditOpen(true); setDayModal(null); }} className="h-7 w-7 p-0"><Pencil className="w-3 h-3" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => { deleteShift(s.shift_id); setDayModal(null); }} className="h-7 w-7 p-0 text-red-500"><Trash2 className="w-3 h-3" /></Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Schedule New Shift</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-semibold">Employee *</Label>
              <Select value={form.user_id} onValueChange={v => setForm(f => ({ ...f, user_id: v }))}>
                <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="shift-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.user_id} value={e.user_id}>{e.name} ({ROLE_LABELS[e.employee_role] || e.employee_role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-semibold">Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="shift-date" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">Start Time *</Label>
                <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="shift-start" />
              </div>
              <div>
                <Label className="font-semibold">End Time *</Label>
                <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="shift-end" />
              </div>
            </div>
            <div>
              <Label className="font-semibold">Role</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="shift-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="party_host">Party Host</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-semibold">Linked Booking (optional)</Label>
              <Input value={form.booking_id} onChange={e => setForm(f => ({ ...f, booking_id: e.target.value }))} placeholder="booking_abc123" className="mt-1 rounded-xl border-[#E8E5DF] font-mono" data-testid="shift-booking" />
            </div>
            <div>
              <Label className="font-semibold">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="shift-notes" />
            </div>
            <Button onClick={createShift} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="confirm-create-shift">Create Shift</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Edit Shift</DialogTitle></DialogHeader>
          {editShift && (
            <div className="space-y-4">
              <p className="text-sm text-[#6B6B7A]">{editShift.employee_name}</p>
              <div><Label className="font-semibold">Date</Label><Input type="date" value={editShift.date} onChange={e => setEditShift(p => ({ ...p, date: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="font-semibold">Start</Label><Input type="time" value={editShift.start_time} onChange={e => setEditShift(p => ({ ...p, start_time: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" /></div>
                <div><Label className="font-semibold">End</Label><Input type="time" value={editShift.end_time} onChange={e => setEditShift(p => ({ ...p, end_time: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" /></div>
              </div>
              <div>
                <Label className="font-semibold">Role</Label>
                <Select value={editShift.role} onValueChange={v => setEditShift(p => ({ ...p, role: v }))}>
                  <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="party_host">Party Host</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="font-semibold">Status</Label>
                <Select value={editShift.status || 'scheduled'} onValueChange={v => setEditShift(p => ({ ...p, status: v }))}>
                  <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="font-semibold">Linked Booking</Label><Input value={editShift.booking_id || ''} onChange={e => setEditShift(p => ({ ...p, booking_id: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF] font-mono" /></div>
              <div><Label className="font-semibold">Notes</Label><Textarea value={editShift.notes || ''} onChange={e => setEditShift(p => ({ ...p, notes: e.target.value }))} rows={2} className="mt-1 rounded-xl border-[#E8E5DF]" /></div>
              <Button onClick={updateShift} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="confirm-edit-shift">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EmailScheduleModal open={emailModalOpen} onClose={() => setEmailModalOpen(false)} />
    </div>
  );
};

// ---------- Calendar Month View ----------
const CalendarMonthView = ({ year, month, onPrev, onNext, onToday, dayTotals, grouped, onDayClick }) => {
  const first = new Date(year, month, 1);
  const monthName = first.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
  // Start on Sunday: fill pre-days
  const startDow = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayIso = new Date().toISOString().split('T')[0];
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = new Date(year, month, d).toLocaleDateString('en-CA');
    cells.push({ d, iso });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const groupedMap = Object.fromEntries(grouped);

  return (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden" data-testid="shifts-calendar">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8E5DF] bg-[#FFFDF8]">
        <p className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{monthName}</p>
        <div className="flex gap-1">
          <button onClick={onPrev} className="px-3 py-1 rounded-full hover:bg-white border border-[#E8E5DF] text-xs" data-testid="cal-prev">‹</button>
          <button onClick={onToday} className="px-3 py-1 rounded-full hover:bg-white border border-[#E8E5DF] text-xs" data-testid="cal-today">Today</button>
          <button onClick={onNext} className="px-3 py-1 rounded-full hover:bg-white border border-[#E8E5DF] text-xs" data-testid="cal-next">›</button>
        </div>
      </div>
      <div className="grid grid-cols-7 text-xs font-semibold text-[#6B6B7A] uppercase bg-[#FAFAF7] border-b border-[#E8E5DF]">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(w => (
          <div key={w} className="px-2 py-2 text-center">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="h-24 border-b border-r border-[#F0EEEA] bg-[#FAFAF7]/40" />;
          const t = dayTotals[c.iso];
          const isToday = c.iso === todayIso;
          const isPast = c.iso < todayIso;
          // Coverage color
          let badgeCls = 'bg-gray-100 text-gray-500';
          if (t) {
            if (t.count >= 3) badgeCls = 'bg-[#06D6A0]/20 text-[#06D6A0]';
            else if (t.count >= 1) badgeCls = 'bg-[#FFD166]/25 text-[#B8860B]';
            else badgeCls = 'bg-red-100 text-red-500';
          }
          return (
            <button
              key={i}
              onClick={() => onDayClick(c.iso, groupedMap[c.iso] || [])}
              className={`h-24 p-2 text-left border-b border-r border-[#F0EEEA] flex flex-col hover:bg-[#FFF8E7] transition-colors ${isToday ? 'bg-[#FF3B6B]/5 ring-inset ring-2 ring-[#FF3B6B]/20' : ''} ${isPast && !t ? 'opacity-60' : ''}`}
              data-testid={`cal-day-${c.iso}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${isToday ? 'text-[#FF3B6B]' : 'text-[#1E1E24]'}`}>{c.d}</span>
                {t && (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badgeCls}`}>
                    {t.count} shift{t.count !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {t && <span className="text-[10px] text-[#6B6B7A] mt-auto">{t.hours}h total</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CrmShifts;
